import { useMemo } from 'react';
import {
  Badge,
  Group,
  Stack,
  Text,
  Title,
  Tree,
  getTreeExpandedState,
  useTree,
} from '@mantine/core';
import type { TreeNodeData } from '@mantine/core';
import type { LocationNodeSummary } from '@isg/shared-types';
import { useCompanies, useLocationsTumu, useWorkplacesTumu } from '../api/queries';
import { ISYERI_DURUM_ETIKET, LOKASYON_TIP_ETIKET } from '../i18n/tr';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * Organizasyon ağacı (F1·S10 ana ekranı): holding → şirket → işyeri →
 * lokasyon dalları. Kullanıcı yalnız bahçesini görür — filtre API'de,
 * ekran ikinci filtre yazmaz. Arşivli işyeri görsel olarak AYRIK: soluk
 * metin + "Arşivde" rozeti (Kapsam §10 — okuma serbest, ayrım görünür).
 */
export function OrgTreeScreen(): React.JSX.Element {
  // ✏️ **TUR 9 / B4 (T9-19): ÜÇ KADEMELİ FAN-OUT İKİ İSTEĞE İNDİ.** Ağaç
  // eskiden şirketler → **her şirket için** işyerleri → **her işyeri için**
  // düğümler diye açılıyordu; otuz düğümlük kurguda bile onlarca istek.
  const companies = useCompanies();
  const isyeriSorgusu = useWorkplacesTumu();
  const workplaces = isyeriSorgusu.data;
  const lokasyonSorgusu = useLocationsTumu();
  const locationsReady = lokasyonSorgusu.isSuccess;

  const data: TreeNodeData[] = useMemo(() => {
    if (!companies.data || !workplaces || !locationsReady) return [];
    const locationsByWorkplace = new Map<string, LocationNodeSummary[]>();
    for (const node of lokasyonSorgusu.data ?? []) {
      const list = locationsByWorkplace.get(node.workplaceId) ?? [];
      list.push(node);
      locationsByWorkplace.set(node.workplaceId, list);
    }
    return companies.data.map((company) => ({
      value: `company:${company.id}`,
      label: company.ad,
      children: workplaces
        .filter((w) => w.companyId === company.id)
        .map((w) => ({
          value: `workplace:${w.id}`,
          label: w.ad,
          nodeProps: { durum: w.durum },
          children: locationChildren(locationsByWorkplace.get(w.id) ?? [], null),
        })),
    }));
  }, [companies.data, workplaces, locationsReady, lokasyonSorgusu.data]);

  return (
    <Stack>
      <Title order={3}>Organizasyon ağacı</Title>
      <QueryStatus queries={[companies, isyeriSorgusu, lokasyonSorgusu]} />
      {companies.data?.length === 0 && (
        <BosListeNotu mesaj="Bahçenizde görüntülenecek şirket yok — kapsam ataması için holding yöneticinize başvurun." />
      )}
      {data.length > 0 && <LoadedOrgTree data={data} />}
    </Stack>
  );
}

/**
 * Ağaç, veri HAZIR olduktan sonra mount edilir; useTree o anda tüm düğümler
 * AÇIK başlar (getTreeExpandedState '*'). Efektle sonradan açmak iki kez
 * yandı (e2e): expandAllNodes henüz-boş state'i kopyalıyor, koşulsuz efekt
 * ise render döngüsüne giriyor — mount-anında başlangıç durumu ikisini de
 * kökten çözer. Kullanıcı düğümleri serbestçe kapatıp açabilir.
 */
function LoadedOrgTree({ data }: { data: TreeNodeData[] }): React.JSX.Element {
  const tree = useTree({ initialExpandedState: getTreeExpandedState(data, '*') });
  return (
    <Tree
      data={data}
      tree={tree}
      levelOffset="lg"
      data-testid="org-agaci"
      renderNode={({ node, expanded, hasChildren, elementProps }) => {
        const durum = (node.nodeProps as { durum?: string } | undefined)?.durum;
        const arsivde = durum === 'archived';
        const tip = (node.nodeProps as { tip?: string } | undefined)?.tip;
        return (
          <Group gap={6} py={3} {...elementProps}>
            {hasChildren && (
              <Text size="xs" c="dimmed" w={12}>
                {expanded ? '▾' : '▸'}
              </Text>
            )}
            <Text size="sm" c={arsivde ? 'dimmed' : undefined} fs={arsivde ? 'italic' : undefined}>
              {node.label}
            </Text>
            {arsivde && (
              <Badge size="xs" color={ANLAM.bilgi} variant="light" data-testid="arsiv-rozeti">
                {ISYERI_DURUM_ETIKET['archived']}
              </Badge>
            )}
            {tip === 'temporary' && (
              <Badge size="xs" color={ANLAM.bilgi} variant="light">
                {LOKASYON_TIP_ETIKET['temporary']}
              </Badge>
            )}
          </Group>
        );
      }}
    />
  );
}

function locationChildren(nodes: LocationNodeSummary[], parentId: string | null): TreeNodeData[] {
  return nodes
    .filter((node) => node.parentId === parentId)
    .map((node) => ({
      value: `location:${node.id}`,
      label: node.kot === null ? node.ad : `${node.ad}`,
      nodeProps: { tip: node.tip },
      children: locationChildren(nodes, node.id),
    }));
}
